import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronLeft, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { NFLBonusesSection } from '@/components/nfl/bonuses/NFLBonusesSection';
import NFLActivityFeed from '@/components/nfl/NFLActivityFeed';
import { BonusesPanel } from '@/components/nfl/bonuses/BonusesPanel';
import { SurvivorPanel } from '@/components/nfl/bonuses/SurvivorPanel';
import { WalletBonusesSection } from '@/components/nfl/bonuses/WalletBonusesSection';

/**
 * SAB-288 Phase 4 integration test page.
 *
 * Hardcoded to the demo NFL league. Real auth (logged-in user's JWT),
 * real reads from nfl_leagues + nfl_teams + league_bonuses +
 * league_survivor_config. RLS exercises normally; isCommissioner is
 * computed from the live league row.
 *
 * The previous /nfldemo wired NFLSettingsPage through
 * NFLLeagueProviderFromValues with a hardcoded league object — that's
 * why the diagnostic logs from commit 40a84fd never fired against real
 * data. This page renders NFLBonusesSection directly with props read
 * from the live DB.
 */
const DEMO_LEAGUE_ID = '1b6fbfa5-f6fc-4601-976b-d9f25097071c';

interface DemoLeague {
  id: string;
  name: string;
  prize_pool: number | null;
  current_week: number | null;
  playoff_start_week: number | null;
  commissioner_id: string;
}

interface DemoTeam {
  id: string;
  name: string;
  owner_id: string;
}

export default function NFLDemo() {
  const { user, isLoading: authLoading } = useAuth();
  const [league, setLeague] = useState<DemoLeague | null>(null);
  const [teams, setTeams] = useState<DemoTeam[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadErr(null);
      try {
        const [leagueRes, teamsRes] = await Promise.all([
          supabase
            .from('nfl_leagues' as any)
            .select(
              'id, name, prize_pool, current_week, playoff_start_week, commissioner_id',
            )
            .eq('id', DEMO_LEAGUE_ID)
            .single(),
          supabase
            .from('nfl_teams' as any)
            .select('id, name, owner_id')
            .eq('league_id', DEMO_LEAGUE_ID)
            .order('name'),
        ]);
        if (cancelled) return;
        if (leagueRes.error) throw leagueRes.error;
        if (teamsRes.error) throw teamsRes.error;
        setLeague((leagueRes.data ?? null) as DemoLeague | null);
        setTeams(((teamsRes.data ?? []) as unknown) as DemoTeam[]);
      } catch (e) {
        if (!cancelled) setLoadErr((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading demo…
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold mb-2">Sign in required</h1>
          <p className="text-sm text-muted-foreground mb-4">
            The bonuses demo reads live data via RLS. Sign in to your account
            to exercise the real fetch + commissioner gate.
          </p>
          <Link
            to="/auth"
            className="inline-block text-sm text-orange-500 hover:underline"
          >
            Go to sign in →
          </Link>
        </div>
      </div>
    );
  }

  if (loadErr || !league) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-6">
        <div className="max-w-md text-center text-sm text-red-400">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3" />
          <div className="font-bold mb-1">Could not load demo league</div>
          <div className="text-muted-foreground">{loadErr ?? 'Not found'}</div>
        </div>
      </div>
    );
  }

  const isCommissioner = league.commissioner_id === user.id;

  return (
    <div className="min-h-screen bg-gradient-dark">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Home
          </Link>
          <div className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">
            SAB-288 · Phase 4 integration test
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        <NFLBonusesSection
          leagueId={league.id}
          prizePool={Number(league.prize_pool ?? 0)}
          currentWeek={league.current_week ?? 1}
          playoffStartWeek={league.playoff_start_week ?? 15}
          teams={teams}
          isCommissioner={isCommissioner}
        />
        <section>
          <NFLActivityFeed leagueId={league.id} />
        </section>

        {/* Phase 6 — League-side panels (lives on production NFLStandingsPage
            as Bonuses + Survivor tabs). Stacked here for verification. */}
        <section>
          <h2 className="text-lg font-bold tracking-tight mb-3">
            Bonuses (league view)
          </h2>
          <BonusesPanel
            leagueId={league.id}
            teams={teams}
            currentWeek={league.current_week ?? 1}
          />
        </section>

        <section>
          <h2 className="text-lg font-bold tracking-tight mb-3">Survivor</h2>
          <SurvivorPanel leagueId={league.id} teams={teams} />
        </section>

        {/* Phase 6 — User-side wallet bonus history. Lives on production
            wallet view as a new section below the existing transactions. */}
        <section>
          <h2 className="text-lg font-bold tracking-tight mb-3">My Wallet</h2>
          <WalletBonusesSection userId={user.id} leagueId={league.id} />
        </section>
      </main>
    </div>
  );
}
